import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  HighlightCard,
  Info,
  LoadingComponent,
  PageHeader,
  SelectInput,
  StatsCard,
  Table,
} from '@demandlocal/components'

import { ReactComponent as PerformanceCPUserPathStart } from './../../../../images/performance/performance-cp-user-start.svg'
import { ReactComponent as PerformanceCPLineTo } from './../../../../images/performance/performance-cp-line-to.svg'
import { ReactComponent as IconFrequency } from '../../../../images/performance/icon_frequency.svg'
import { ReactComponent as IconUserPath } from '../../../../images/performance/icon_user_path.svg'
import { ReactComponent as IconSiteConversionPath } from '../../../../images/performance/icon_site_conversion_path.svg'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'

import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import BarChart from '../../../../components/Charts/BarChart'
import { useGetConversionPathsOverallMetrics } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsOverallMetrics'
import { formatNumber, formatPercentage } from '../../../../lib/utils/formatNumber'
import { useGetConversionPathsUserPath } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsUserPath'
import { useGetConversionpathsCrossDeviceResults } from '../../../../api/dealership/performance/conversionPaths/useGetConversionpathsCrossDeviceResults'
import classNames from 'classnames'
import { useGetConversionPathsInteractionStats } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsInteractionStats'
import { useGetConversionPathsConversionsByFrequency } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsConversionByFrequency'
import { FormProvider, useForm } from 'react-hook-form'
import { useGetConversionPathsTrackingTags } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsTrackingTags'
import { usePrevious } from '../../../../lib/hooks/usePrevious'
import { debounce, isEmpty, isEqual } from 'lodash'
import HorizontalBarChart from '../../../../components/Charts/HorizontalBarChart'
import { useGetConversionPathsConversionsBySite } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsConversionsBySite'
import { createGradient } from '../../../../lib/utils/chartJS'
import UserPathStep from '../../../../components/Performance/ConversionPaths/UserPathStep'
import { useGetConversionPathsConversionsByUTM } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsConversionsByUTM'
import { useSearchParams } from 'react-router-dom'
import { createColumnHelper, Row } from '@tanstack/react-table'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useGetConversionPathsUsers } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathUsers'
import { useGetConversionPathUsersFilters } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathUsersFilters'

const colors = ['#70EC8C', '#7E77C9', '#FFC90C', '#5317D1', '#B60000', '#FC5555']

type FormValues = {
  period: {
    label: string
    value: string
  }
  tag?: Array<{
    label: string
    value: string
  }>
}

const periodOptions = [
  {
    label: 'Last month',
    value: 'last_month',
  },
  {
    label: 'Last 3 months',
    value: 'last_3_months',
  },
  {
    label: 'Last 6 months',
    value: 'last_6_months',
  },
  {
    label: 'Year to date',
    value: 'year_to_date',
  },
  {
    label: 'Month to date',
    value: 'month_to_date',
  },
]

const PerformanceConversionPathsFilters = ({
  setFilters,
}: {
  setFilters: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}): JSX.Element => {
  const { data: trackingTags } = useGetConversionPathsTrackingTags()
  const trackingTagOptions = useMemo(() => {
    if (!trackingTags) return []
    return trackingTags.map((tag) => ({
      label: tag.conversion_tracking_tag_name,
      value: tag.conversion_tracking_tag_id,
    }))
  }, [trackingTags])

  const [searchParams, setSearchParams] = useSearchParams()

  const formContext = useForm<FormValues>({
    defaultValues: () => {
      const period = searchParams.get('period')
      return new Promise((resolve) => {
        resolve({
          period: period
            ? periodOptions.find((option) => option.value === period) || periodOptions[0]
            : periodOptions[0],
        })
      })
    },
  })

  const { watch, setValue } = formContext

  const formValues = formContext.watch()
  const tagValue = watch('tag')
  const prevFormValues = usePrevious(formValues)
  const debouncedSetColumnFilters = debounce(setFilters, 300)

  useEffect(() => {
    if (!isEqual(prevFormValues, formValues)) debouncedSetColumnFilters(formValues)
  }, [formValues, prevFormValues, debouncedSetColumnFilters])

  useEffect(() => {
    if (isEmpty(tagValue) && !isEmpty(trackingTagOptions)) {
      const trackingTags = searchParams.getAll('tag')
      if (trackingTags.length > 0) {
        setValue(
          'tag',
          trackingTagOptions.filter((option) => trackingTags.includes(option.value)),
        )
      } else {
        setValue('tag', trackingTagOptions[0])
        setSearchParams((sp) => {
          sp.set('tag', trackingTagOptions[0].value)
          return sp
        })
      }
    }
  }, [setValue, tagValue, trackingTagOptions, setSearchParams, searchParams])

  return (
    <FormProvider {...formContext}>
      <form className={'print:hidden'}>
        <div
          className={
            'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
          }>
          <div className={'grid grid-cols-3 gap-5'}>
            <SelectInput
              isMulti={true}
              required={true}
              name={'tag'}
              onChange={(value) => {
                setSearchParams((sp) => {
                  if (sp.has('tag')) sp.delete('tag')
                  value.forEach((v) => sp.append('tag', v.value))
                  return sp
                })
              }}
              label={'Conversion Goals'}
              options={trackingTagOptions}
            />
            <SelectInput
              required={true}
              name={'period'}
              label={'Period'}
              onChange={(value) => {
                setSearchParams((sp) => {
                  sp.set('period', value.value)
                  return sp
                })
              }}
              options={periodOptions}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

const UsersTableFilters = ({
  filters,
  onFiltersChange,
}: {
  filters: Record<string, any>
  onFiltersChange: (filters: Record<string, any>) => void
}) => {
  const formContext = useForm<FormValues>({})
  const { watch } = formContext
  const { data: siteFilters } = useGetConversionPathUsersFilters({
    filters: {
      ...filters,
      type: 'site',
    },
  })
  const { data: deviceFilters } = useGetConversionPathUsersFilters({
    filters: {
      ...filters,
      type: 'device',
    },
  })
  const { data: campaignFilters } = useGetConversionPathUsersFilters({
    filters: {
      ...filters,
      type: 'campaign',
    },
  })
  const siteOptions = useMemo(() => {
    return siteFilters?.map((site) => ({
      label: site.event_site,
      value: site.event_site,
    }))
  }, [siteFilters])
  const deviceOptions = useMemo(() => {
    return deviceFilters?.map((device) => ({
      label: device.event_device_type,
      value: device.event_device_type,
    }))
  }, [deviceFilters])
  const campaignOptions = useMemo(() => {
    return campaignFilters?.map((campaign) => ({
      label: campaign.event_campaign_name,
      value: campaign.event_campaign_id,
    }))
  }, [campaignFilters])

  useEffect(() => {
    const subscription = watch((newValue) => {
      onFiltersChange(newValue)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [watch, onFiltersChange])

  return (
    <FormProvider {...formContext}>
      <form className={'print:hidden'}>
        <div
          className={
            'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
          }>
          <div className={'grid grid-cols-3 gap-5'}>
            <SelectInput
              isMulti={true}
              name={'site'}
              label={'Site'}
              options={siteOptions}
            />
            <SelectInput
              isMulti={true}
              name={'device'}
              label={'Device'}
              options={deviceOptions}
            />
            <SelectInput
              isMulti={true}
              name={'campaign'}
              label={'Campaign'}
              options={campaignOptions}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

const UserPath = ({
  row,
  filters,
}: {
  row: Row<any>
  filters?: Record<string, unknown>
}) => {
  const {
    data: userPath,
    isLoading: isLoadingUserPath,
    isPreviousData: isPreviousDataUserPath,
  } = useGetConversionPathsUserPath({
    filters,
    tdid: row.original.conversion_tdid,
  })

  if (isLoadingUserPath) {
    return (
      <div className={'h-40 relative'}>
        <LoadingComponent title={'Loading...'} />
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'bg-primary-persian-blue/10 p-5 rounded-md flex flex-col gap-5',
        {
          'opacity-50': isPreviousDataUserPath,
        },
      )}>
      <div className={'flex flex-row gap-2 items-center'}>
        <IconUserPath />
        <h3 className={'font-bold text-base'}>User path</h3>
      </div>
      <div className={'flex flex-row items-center justify-center gap-8'}>
        <div className={'flex items-center overflow-x-scroll w-full pb-4'}>
          <div
            className={
              'bg-white border-2 border-primary-xiketic rounded-2xl p-5 items-center flex flex-col justify-center gap-3 min-w-fit'
            }>
            <PerformanceCPUserPathStart />
            <div className={'mx-auto text-center'}>
              <h3 className={'text-primary-xiketic font-bold text-sm'}>User</h3>
            </div>
          </div>
          {userPath?.map((path, index, array) => {
            return (
              <div key={index} className={'flex items-center'}>
                <div className={'-ml-0.5 -mr-2'}>
                  <PerformanceCPLineTo
                    className={index % 2 === 0 ? '-scale-y-100' : ''}
                  />
                </div>
                {index < array.length && <UserPathStep path={path} />}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const conversionsByUTMColumnHelper = createColumnHelper<any>()
const usersColumns = createColumnHelper<{
  conversion_tdid: string
  conversion_count: number
  impressions: number
  clicks: number
}>()
export default function PerformanceConversionPaths(): JSX.Element {
  const dealership = useDealership()
  const [filters, setFilters] = useState<Record<string, unknown>>({})
  const [userFilters, setUserFilters] = useState<Record<string, unknown>>({})
  const leastRequiredFiltersNotEmpty = useMemo(() => {
    return !isEmpty(filters?.tag) && !isEmpty(filters?.period)
  }, [filters])

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const {
    data: conversionPathsMetrics,
    isPreviousData: isPreviousDataOverallMetrics,
    isLoading: isLoadingConversionPathsMetrics,
  } = useGetConversionPathsOverallMetrics({
    filters,
  })
  const {
    data: userPath,
    isLoading: isLoadingUserPath,
    isPreviousData: isPreviousDataUserPath,
  } = useGetConversionPathsUserPath({
    filters,
  })
  const {
    data: crossDeviceResults,
    isLoading: isLoadingCrossDeviceResults,
    isPreviousData: isPreviousDataCrossDeviceResults,
    isFetching: isFetchingCrossDeviceResults,
  } = useGetConversionpathsCrossDeviceResults({
    filters,
  })

  const {
    data: conversionsByUTM,
    isPreviousData: isPreviousDataConversionsByUTM,
    isLoading: isLoadingConversionsByUTM,
  } = useGetConversionPathsConversionsByUTM({
    filters,
  })
  const {
    data: conversionByFrequency,
    isPreviousData: isPreviousDataConversionByFrequency,
    isLoading: isLoadingConversionByFrequency,
  } = useGetConversionPathsConversionsByFrequency({
    filters,
  })
  const {
    data: eventDeviceInteractionStats,
    isPreviousData: isPreviousDataInteractionStatsByEventDevice,
    isLoading: isLoadingEventDeviceInteractionStatsByEventDevice,
  } = useGetConversionPathsInteractionStats({
    filters,
    breakdown: 'event_device_type',
  })

  const {
    data: eventSiteInteractionStats,
    isPreviousData: isPreviousDataInteractionStatsByEventSite,
    isLoading: isLoadingEventSiteInteractionStatsByEventSite,
  } = useGetConversionPathsInteractionStats({
    filters,
    breakdown: 'event_site',
  })
  const {
    data: conversionsBySite,
    isPreviousData: isPreviousDataConversionsBySite,
    isLoading: isLoadingConversionsBySite,
  } = useGetConversionPathsConversionsBySite({
    filters,
  })

  const conversionsByUTMColumns = useMemo(
    () => [
      conversionsByUTMColumnHelper.accessor('utm_source', {
        header: 'UTM source',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      conversionsByUTMColumnHelper.accessor('people', {
        header: () => (
          <div className="flex flex-row gap-2 items-center">
            <span>People</span>
            <Info message="Number of people who converted that had the respective utm source on their path" />
          </div>
        ),
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      conversionsByUTMColumnHelper.accessor('percentage_of_total', {
        header: () => (
          <div className="flex flex-row gap-2 items-center">
            <span>% of People</span>
            <Info message="% of people who converted that had the respective utm source on their path" />
          </div>
        ),
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      conversionsByUTMColumnHelper.accessor('average', {
        header: () => (
          <div className="flex flex-row gap-2 items-center">
            <span>Avg/Person</span>
            <Info message="Average number of times the utm source appeared on the path per person" />
          </div>
        ),
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
    ],
    [],
  )

  const fewestNumbersOfImpressionsPerConversionByDevice = useMemo(() => {
    return crossDeviceResults?.reduce(
      (acc, cur) => {
        if (cur.avg_impressions_per_conversion < acc.avg_impressions_per_conversion) {
          return {
            ...acc,
            avg_impressions_per_conversion: cur.avg_impressions_per_conversion,
            event_device_type: cur.event_device_type,
          }
        } else {
          return acc
        }
      },
      {
        event_device_type: undefined,
        avg_impressions_per_conversion: Infinity,
      } as {
        event_device_type: string | undefined
        avg_impressions_per_conversion: number
      },
    )
  }, [crossDeviceResults])

  const fewestNumbersOfImpressionsPerConversionBySite = useMemo(() => {
    return conversionsBySite?.reduce(
      (acc, cur) => {
        if (cur.avg_impressions_per_conversion < acc.avg_impressions_per_conversion) {
          return {
            ...acc,
            avg_impressions_per_conversion: cur.avg_impressions_per_conversion,
            event_site: cur.event_site,
          }
        } else {
          return acc
        }
      },
      {
        event_site: undefined,
        avg_impressions_per_conversion: Infinity,
      },
    )
  }, [conversionsBySite])

  const shortestTimeToConvertBySite = useMemo(() => {
    return conversionsBySite?.reduce(
      (acc, cur) => {
        if (cur.avg_days_to_conversion < acc.avg_days_to_conversion) {
          return {
            ...acc,
            avg_days_to_conversion: cur.avg_days_to_conversion,
            event_site: cur.event_site,
          }
        } else {
          return acc
        }
      },
      {
        event_site: undefined,
        avg_days_to_conversion: Infinity,
      },
    )
  }, [conversionsBySite])

  const shortestTimeToConvertByDevice = useMemo(() => {
    return crossDeviceResults?.reduce(
      (acc, cur) => {
        if (cur.avg_days_to_conversion < acc.avg_days_to_conversion) {
          return {
            ...acc,
            avg_days_to_conversion: cur.avg_days_to_conversion,
            event_device_type: cur.event_device_type,
          }
        } else {
          return acc
        }
      },
      {
        event_device_type: undefined,
        avg_days_to_conversion: Infinity,
      } as {
        event_device_type: string | undefined
        avg_days_to_conversion: number
      },
    )
  }, [crossDeviceResults])

  const [isExpanded, setIsExpanded] = useState(true)

  const isLoadingReport =
    !leastRequiredFiltersNotEmpty ||
    isLoadingConversionPathsMetrics ||
    isLoadingUserPath ||
    isLoadingCrossDeviceResults ||
    isLoadingConversionsByUTM ||
    isLoadingConversionByFrequency ||
    isLoadingEventDeviceInteractionStatsByEventDevice ||
    isLoadingEventSiteInteractionStatsByEventSite ||
    isLoadingConversionsBySite

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  const userColumns = useMemo(
    () => [
      usersColumns.accessor('conversion_tdid', {
        header: 'User ID',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.conversion_tdid}
            </button>
          ) : (
            row.original.conversion_tdid
          )
        },
      }),
      usersColumns.accessor('conversion_count', {
        header: 'Conversions',
        cell: ({ row }) => row.original.conversion_count,
      }),
      usersColumns.accessor('impressions', {
        header: 'Impressions',
        cell: ({ row }) => row.original.impressions,
      }),
    ],
    [],
  )

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        cardsOptions={[]}
        title={'Performance'}
        rightSlot={() => (
          <div className={'flex gap-2 print:hidden'}>
            <Button
              variant={'tertiary'}
              onClick={(e) => {
                e.preventDefault()
                setIsExpanded((prev) => !prev)
              }}>
              Filters
            </Button>
            <Button
              variant={'rainbow'}
              disabled={isDownloadingReport || isLoadingReport}
              rightSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                const toastId = toast.loading('Generating report...')
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `Performance_Conversion_Paths_${
                      (filters?.period as FormValues['period']).value
                    }.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'flex flex-col gap-8 pb-8'}>
          {isExpanded && <PerformanceConversionPathsFilters setFilters={setFilters} />}
          <div
            className={classNames('grid grid-cols-6 auto-rows-fr gap-4', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataOverallMetrics,
            })}>
            <StatsCard
              title={'Impressions'}
              content={formatNumber(conversionPathsMetrics?.impressions, {
                maximumFractionDigits: 2,
              })}
              color={'bg-secondary-malachite-green/40'}
            />
            <StatsCard
              title={'Clicks'}
              content={formatNumber(conversionPathsMetrics?.clicks, {
                maximumFractionDigits: 2,
              })}
              color={'bg-secondary-malachite-green/40'}
            />
            <StatsCard
              title={'Impressions per converting user'}
              content={formatNumber(
                conversionPathsMetrics?.impressions_per_converting_user,
                {
                  maximumFractionDigits: 2,
                },
              )}
              color={'bg-secondary-malachite-green/40'}
            />
            <StatsCard
              title={'Impressions per conversion'}
              content={formatNumber(conversionPathsMetrics?.impressions_per_conversion, {
                maximumFractionDigits: 2,
              })}
              color={'bg-secondary-malachite-green/40'}
            />
            <StatsCard
              title={'Avg. Days From First Impression to Conversion'}
              content={formatNumber(
                conversionPathsMetrics?.avg_days_from_first_impression,
                {
                  maximumFractionDigits: 2,
                },
              )}
              color={'bg-secondary-malachite-green/40'}
            />
            <StatsCard
              title={'Avg. Days From Last Impression to Conversion'}
              content={formatNumber(
                conversionPathsMetrics?.avg_days_from_last_impression,
                {
                  maximumFractionDigits: 2,
                },
              )}
              color={'bg-secondary-malachite-green/40'}
            />
          </div>

          <div
            className={classNames('grid grid-cols-3 auto-rows-fr gap-4', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataOverallMetrics,
            })}>
            <StatsCard
              title={'Number of conversions'}
              content={formatNumber(conversionPathsMetrics?.conversion_count, {
                maximumFractionDigits: 2,
              })}
              color={'bg-primary-persian-blue/10'}
            />
            <StatsCard
              title={'Number of users'}
              content={formatNumber(conversionPathsMetrics?.tdid_count, {
                maximumFractionDigits: 2,
              })}
              color={'bg-primary-persian-blue/10'}
            />
            <StatsCard
              title={'Conversions per person'}
              content={formatNumber(conversionPathsMetrics?.conv_per_person, {
                maximumFractionDigits: 2,
              })}
              color={'bg-primary-persian-blue/10'}
            />
          </div>

          {!isLoadingUserPath &&
            leastRequiredFiltersNotEmpty &&
            !dealership?.enable_path_to_conversion_user_list && (
              <div
                className={classNames(
                  'bg-primary-persian-blue/10 p-5 overflow-x-scroll rounded-md flex flex-col gap-5',
                  {
                    'opacity-50': isPreviousDataUserPath,
                  },
                )}>
                <div className={'flex flex-row gap-2 items-center'}>
                  <IconUserPath />
                  <h3 className={'font-bold text-base'}>Sample user path</h3>
                </div>
                <div className={'flex flex-row items-center justify-center gap-8'}>
                  <div className={'flex items-center overflow-x-scroll w-full pb-4'}>
                    <div
                      className={
                        'bg-white border-2 border-primary-xiketic rounded-2xl p-5 items-center flex flex-col justify-center gap-3 min-w-fit'
                      }>
                      <PerformanceCPUserPathStart />
                      <div className={'mx-auto text-center'}>
                        <h3 className={'text-primary-xiketic font-bold text-sm'}>User</h3>
                      </div>
                    </div>
                    {userPath?.map((path, index, array) => {
                      return (
                        <div key={index} className={'flex items-center'}>
                          <div className={'-ml-0.5 -mr-2'}>
                            <PerformanceCPLineTo
                              className={index % 2 === 0 ? '-scale-y-100' : ''}
                            />
                          </div>
                          {index < array.length && <UserPathStep path={path} />}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          {dealership?.enable_path_to_conversion_user_list &&
            leastRequiredFiltersNotEmpty && (
              <div>
                <h3 className={'font-bold text-xl mb-2'}>Users</h3>
                <UsersTableFilters filters={filters} onFiltersChange={setUserFilters} />
                <Table
                  fixed={true}
                  columns={userColumns}
                  filters={{ ...filters, ...userFilters }}
                  fetchFn={useGetConversionPathsUsers}
                  getRowCanExpand={() => true}
                  renderSubComponent={UserPath}
                />
              </div>
            )}
          <div
            className={classNames('flex gap-2 flex-col', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataCrossDeviceResults,
            })}>
            <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-0">
              <thead className="text-xs text-gray-700 uppercase">
                <tr>
                  <th scope="col" className="py-3 px-6">
                    Cross Device Conversion Path
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(`py-3 px-6 bg-gray-100 border-t-2`, {
                      'rounded-tl-md border-l-2': index === 0,
                      'rounded-tr-md border-r-2': index === array.length - 1,
                    })
                    return (
                      <th key={value.event_device_type} scope="col" className={className}>
                        {value.event_device_type}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap border-t-2 border-l-2 rounded-tl-md">
                    Conversions
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(`py-4 px-6 bg-gray-100 border-t-2`, {
                      'border-l-2': index === 0,
                      'border-r-2': index === array.length - 1,
                    })
                    return (
                      <td key={value.event_device_type} scope="col" className={className}>
                        {formatNumber(value.conversions, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    )
                  })}
                </tr>
                <tr className="bg-white">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap border-t-2 border-l-2">
                    Time to convert (days)
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(`py-4 px-6 bg-gray-100 border-t-2`, {
                      'border-l-2': index === 0,
                      'border-r-2': index === array.length - 1,
                    })
                    return (
                      <td key={value.event_device_type} scope="col" className={className}>
                        {formatNumber(value.avg_days_to_conversion, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    )
                  })}
                </tr>

                <tr className="bg-white">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap border-t-2 border-l-2">
                    First impressions
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(`py-4 px-6 bg-gray-100 border-t-2`, {
                      'border-l-2': index === 0,
                      'border-r-2': index === array.length - 1,
                    })
                    return (
                      <td key={value.event_device_type} scope="col" className={className}>
                        {formatNumber(value.percent_first_impression, {
                          maximumFractionDigits: 2,
                        })}
                        %
                      </td>
                    )
                  })}
                </tr>
                <tr className="bg-white">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap border-t-2 border-l-2">
                    Last impressions
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(`py-4 px-6 bg-gray-100 border-t-2`, {
                      'border-l-2': index === 0,
                      'border-r-2': index === array.length - 1,
                    })
                    return (
                      <td key={value.event_device_type} scope="col" className={className}>
                        {formatNumber(value.percent_last_impression, {
                          maximumFractionDigits: 2,
                        })}
                        %
                      </td>
                    )
                  })}
                </tr>
                <tr className="border-2">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap border-t-2 border-l-2 border-b-2 rounded-bl-md">
                    Impressions per conversion
                  </th>
                  {crossDeviceResults?.map((value, index, array) => {
                    const className = classNames(
                      `py-4 px-6 bg-gray-100 border-t-2 border-b-2`,
                      {
                        'border-l-2': index === 0,
                        'border-r-2': index === array.length - 1,
                      },
                    )
                    return (
                      <td key={value.event_device_type} scope="col" className={className}>
                        {formatNumber(value.avg_impressions_per_conversion, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <div
            className={classNames('grid grid-cols-2 gap-8', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataInteractionStatsByEventDevice,
            })}>
            <HighlightCard
              value={formatNumber(
                eventDeviceInteractionStats?.average_number_of_touches ?? 0,
                {
                  maximumFractionDigits: 2,
                },
              )}
              content={
                <span>
                  a user encountered ads on approximately{' '}
                  {formatNumber(
                    eventDeviceInteractionStats?.average_number_of_touches ?? 0,
                    {
                      maximumFractionDigits: 2,
                    },
                  )}{' '}
                  devices before making a conversion.
                </span>
              }
            />
            <HighlightCard
              value={formatPercentage(
                (Number(
                  eventDeviceInteractionStats?.percent_conversions_with_more_than_one_grain,
                ) || 0) / 100 || 0,
                2,
              )}
              content={
                <span>
                  a user was served an ad on more than one device before converting.
                </span>
              }
            />
          </div>
          <div
            className={classNames('grid grid-cols-2 gap-8', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataInteractionStatsByEventSite,
            })}>
            <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
              <h1 className={'text-sm font-bold'}>
                {shortestTimeToConvertByDevice?.event_device_type} conversions
              </h1>
              <p>
                When <b>{shortestTimeToConvertByDevice?.event_device_type}</b> was on the
                conversion path, <b>the time to convert was the shortest</b>. On average,
                it only took{' '}
                <b>{shortestTimeToConvertByDevice?.avg_days_to_conversion}</b> days for a
                user to convert after the last impression.
              </p>
            </div>
            <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
              <h1 className={'text-sm font-bold'}>
                {fewestNumbersOfImpressionsPerConversionByDevice?.event_device_type}{' '}
                impressions
              </h1>
              <p>
                Users needed the <b>fewest number of impressions to convert</b> when{' '}
                <b>
                  {fewestNumbersOfImpressionsPerConversionByDevice?.event_device_type}
                </b>{' '}
                was on the conversion path - it only took an average of{' '}
                {
                  fewestNumbersOfImpressionsPerConversionByDevice?.avg_impressions_per_conversion
                }{' '}
                impressions for a user to convert.
              </p>
            </div>
          </div>
          <div
            className={classNames('flex flex-col gap-4', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataConversionByFrequency,
            })}>
            <div className={'flex flex-row items-center justify-between'}>
              <div className={'flex flex-row items-center gap-3'}>
                <IconFrequency />
                <h2 className={'font-bold text-lg'}>Conversion by Frequency</h2>
              </div>
            </div>
            <div className={'p-5 flex flex-col gap-6 border-2 rounded-lg'}>
              <h3 className={'text-sm font-bold'}>
                Frequency distribution - Percent of conversions
              </h3>
              <div className={'h-64'}>
                <MultiLineChart
                  data={conversionByFrequency ?? []}
                  customOptions={{
                    fill: false,
                    elements: {
                      line: {
                        tension: 0,
                      },
                      point: {
                        radius: 0,
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                      tooltip: {
                        mode: 'index' as const,
                        intersect: false,
                      },
                    },
                  }}
                  gradient={[
                    'blue-to-purple',
                    'red-to-orange',
                    'green-to-lemon-green',
                    'green-to-cyan',
                    'blue-to-cyan',
                    'red-to-orange',
                    'green-to-cyan',
                  ]}
                  borderColor="white"
                  showBackground={false}
                  borderWidth={2}
                  groupKey={'tracking_tag'}
                  labelKey={'tracking_tag'}
                  yKey={'percentage_of_total'}
                  xKey={'frequency'}
                />
              </div>
            </div>
            <div className={'p-5 flex flex-col gap-6 border-2 rounded-lg'}>
              <h3 className={'text-sm font-bold'}>
                Frequency distribution - Total Conversion Count
              </h3>
              <div className={'h-64'}>
                <BarChart
                  type="dataset"
                  labels={(
                    [
                      ...new Set(conversionByFrequency?.map((item) => item.frequency)),
                    ] as Array<number>
                  ).sort((a, b) => a - b)}
                  datasets={
                    (conversionByFrequency ?? []).reduce((acc, item) => {
                      const labels = (
                        [
                          ...new Set(
                            conversionByFrequency?.map((item) => item.frequency),
                          ),
                        ] as Array<number>
                      ).sort((a, b) => a - b)
                      const label = item.tracking_tag
                      let existingOption = acc.find((option) => option.label === label)
                      if (!existingOption) {
                        existingOption = {
                          label: item.tracking_tag,
                          data: Array.from({ length: labels.length }, () => 0), // Initialize all frequencies with 0
                          backgroundColor: colors[acc.length % colors.length],
                          fill: true,
                          datalabels: { display: false },
                        }
                        acc.push(existingOption)
                      }
                      const frequencyIndex = labels.findIndex(
                        (label) => label === item.frequency,
                      )
                      if (frequencyIndex !== -1)
                        existingOption.data[frequencyIndex] = item.conversions_count

                      return acc
                    }, []) ?? []
                  }
                  isLoading={false}
                  customOptions={{
                    plugins: {
                      legend: {
                        display: true,
                        position: 'bottom',
                      },
                      tooltip: {
                        mode: 'index' as const,
                        intersect: false,
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        grid: {
                          display: false,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          display: true,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={classNames('flex flex-col gap-4', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataInteractionStatsByEventSite,
            })}>
            <div className={'flex flex-row items-center justify-between'}>
              <div className={'flex flex-row items-center gap-3'}>
                <IconSiteConversionPath />
                <h2 className={'font-bold text-lg'}>Site Conversion Path</h2>
              </div>
            </div>
            <div
              className={classNames('grid grid-cols-2 gap-8', {
                hidden: !leastRequiredFiltersNotEmpty,
                'opacity-50': isPreviousDataInteractionStatsByEventSite,
              })}>
              <HighlightCard
                value={formatNumber(
                  eventSiteInteractionStats?.average_number_of_touches ?? 0,
                  {
                    maximumFractionDigits: 2,
                  },
                )}
                content={
                  <span>
                    a user encountered ads on approximately{' '}
                    {formatNumber(
                      eventSiteInteractionStats?.average_number_of_touches ?? 0,
                      {
                        maximumFractionDigits: 2,
                      },
                    )}{' '}
                    sites before making a conversion.
                  </span>
                }
              />
              <HighlightCard
                value={formatPercentage(
                  (Number(
                    eventSiteInteractionStats?.percent_conversions_with_more_than_one_grain,
                  ) || 0) / 100 || 0,
                  2,
                )}
                content={
                  <span>
                    a user was served an ad on more than one site before converting.
                  </span>
                }
              />
            </div>
            <div
              className={classNames('grid grid-cols-2 gap-8', {
                hidden: !leastRequiredFiltersNotEmpty,
                'opacity-50': isPreviousDataInteractionStatsByEventSite,
              })}>
              <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
                <h1 className={'text-sm font-bold'}>
                  {shortestTimeToConvertBySite?.event_site} conversions
                </h1>
                <p>
                  When <b>{shortestTimeToConvertBySite?.event_site}</b> was on the
                  conversion path, <b>the time to convert was the shortest</b>. On
                  average, it only took{' '}
                  <b>{shortestTimeToConvertBySite?.avg_days_to_conversion}</b> days for a
                  user to convert after the last impression.
                </p>
              </div>
              <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
                <h1 className={'text-sm font-bold'}>
                  {fewestNumbersOfImpressionsPerConversionBySite?.event_site} impressions
                </h1>
                <p>
                  Users needed the <b>fewest number of impressions to convert</b> when{' '}
                  <b>{fewestNumbersOfImpressionsPerConversionBySite?.event_site}</b> was
                  on the conversion path - it only took an average of{' '}
                  {
                    fewestNumbersOfImpressionsPerConversionBySite?.avg_impressions_per_conversion
                  }{' '}
                  impressions for a user to convert.
                </p>
              </div>
            </div>
          </div>
          <div
            className={classNames('p-5 flex flex-col gap-6 border-2 rounded-lg', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataConversionsBySite,
            })}>
            <h3 className={'text-sm font-bold'}>First and Last impressions by site</h3>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                key={'first-last-impressions'}
                type="dataset"
                labels={(conversionsBySite ?? []).map((item, index) => item.event_site)}
                datasets={[
                  {
                    label: 'First Impressions',
                    borderRadius: 8,
                    data: (conversionsBySite ?? []).map((data) => data.first_impressions),
                    backgroundColor: createGradient(
                      ['#06D2EE80', '#70EC8C'],
                      'horizontal',
                    ),
                    borderColor: createGradient(['#06D2EE80', '#70EC8C'], 'horizontal'),
                    borderWidth: 1,
                  },
                  {
                    label: 'Last Impressions',
                    borderRadius: 8,
                    data: (conversionsBySite ?? []).map((data) => data.last_impressions),
                    backgroundColor: '#7E77C9',
                    borderColor: '#7E77C9',
                    borderWidth: 1,
                  },
                ]}
                isLoading={false}
                customOptions={{
                  plugins: {
                    datalabels: {
                      display: false,
                    },
                    legend: { display: false },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                  scales: {
                    xAxes: [
                      {
                        display: false,
                      },
                    ],
                    yAxes: [
                      {
                        display: false,
                      },
                    ],
                  },
                }}
              />
            </div>
          </div>
          <div
            className={classNames('p-5 flex flex-col gap-6 border-2 rounded-lg', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataConversionsBySite,
            })}>
            <h3 className={'text-sm font-bold'}>Impressions per conversion by site</h3>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                key={'impressions-per-conversion'}
                isPreviousData={false}
                isLoading={false}
                type="dataset"
                labels={(conversionsBySite ?? []).map((item, index) => item.event_site)}
                datasets={[
                  {
                    label: 'Impressions per conversion',
                    borderRadius: 8,
                    height: 8,
                    data: (conversionsBySite ?? []).map(
                      (data) => data.avg_impressions_per_conversion,
                    ),
                    backgroundColor: createGradient(
                      ['#06D2EE80', '#70EC8C'],
                      'horizontal',
                    ),
                    borderColor: createGradient(['#06D2EE80', '#70EC8C'], 'horizontal'),
                    borderWidth: 1,
                  },
                ]}
                customOptions={{
                  plugins: {
                    datalabels: {
                      display: false,
                    },
                    legend: { display: false },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                  scales: {
                    xAxes: [
                      {
                        display: false,
                      },
                    ],
                    yAxes: [
                      {
                        display: false,
                      },
                    ],
                  },
                }}
              />
            </div>
          </div>
          <div
            className={classNames('p-5 flex flex-col gap-6 border-2 rounded-lg', {
              hidden: !leastRequiredFiltersNotEmpty,
              'opacity-50': isPreviousDataConversionsBySite,
            })}>
            <h3 className={'text-sm font-bold'}>Days to convert</h3>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                key={'days-to-convert'}
                isPreviousData={false}
                isLoading={false}
                type="dataset"
                labels={(conversionsBySite ?? []).map((item, index) => item.event_site)}
                datasets={[
                  {
                    label: 'Avg. Days to Conversion',
                    borderRadius: 8,
                    height: 8,
                    data: (conversionsBySite ?? []).map(
                      (data) => data.avg_days_to_conversion,
                    ),
                    backgroundColor: '#7E77C9',
                    borderColor: '#7E77C9',
                    borderWidth: 1,
                  },
                ]}
                customOptions={{
                  plugins: {
                    datalabels: {
                      display: false,
                    },
                    legend: { display: false },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                  scales: {
                    xAxes: [
                      {
                        display: false,
                      },
                    ],
                    yAxes: [
                      {
                        display: false,
                      },
                    ],
                  },
                }}
              />
            </div>
          </div>
          <div
            className={classNames('flex flex-col gap-4', {
              hidden: !leastRequiredFiltersNotEmpty,
            })}>
            <div className={'flex flex-row items-center justify-between'}>
              <div className={'flex flex-row items-center gap-3'}>
                <IconSiteConversionPath />
                <h2 className={'font-bold text-lg'}>Conversions By UTM</h2>
              </div>
            </div>
            <Table
              hidePagination={true}
              columns={conversionsByUTMColumns}
              filters={filters}
              fetchFn={useGetConversionPathsConversionsByUTM}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
